import { fmt } from "./Utils/Utilities";
import CreateCollectionPage from "./Createcollection";
import React, { useState, useEffect, useCallback } from "react";
import { usePermissions } from "./PermissionsContext";

const API_BASE = "https://api.seritonex.com";
const token = () => localStorage.getItem("token");

const decodeToken = () => {
  try {
    const jwt = token();
    if (!jwt) return null;
    const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const organizationId = () => {
  const payload = decodeToken();
  return parseInt(payload?.organization?.organization_id ?? 1, 10);
};

const getOrgId = organizationId;

const getUserScope = () => {
  const payload = decodeToken();
  if (!payload) return { type: "org" };
  if (payload.supplier_branch_id)
    return {
      type: "supplier_branch",
      id: parseInt(payload.supplier_branch_id, 10),
      isSupplier: false,
    };
  if (payload.branch_id)
    return {
      type: "branch",
      id: parseInt(payload.branch_id, 10),
      isSupplier: false,
    };
  if (payload.warehouse_id)
    return {
      type: "warehouse",
      id: parseInt(payload.warehouse_id, 10),
      isSupplier: false,
    };
  return { type: "org", isSupplier: false };
};

const getLoggedInUserId = () => {
  const payload = decodeToken();
  return payload?.user_id ?? payload?.id ?? payload?.sub ?? null;
};

const apiFetch = async (method, path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json().catch(() => null);
};

const detectJwtRole = () => {
  const payload = decodeToken();
  if (!payload) return "customer";
  if (payload.supplier_branch_id) return "supplier";
  if (payload.branch_id || payload.warehouse_id) return "customer";
  if (payload.is_supplier === true || payload.is_supplier === 1)
    return "supplier";
  return "customer";
};

const getCollectionById = (collection_id) =>
  apiFetch("POST", "/api/v1/amis/collection/by-id", { collection_id });

const getCollectionsByBranch = (branch_id, is_supplier) =>
  apiFetch("POST", "/api/v1/amis/collection/by-branch", {
    branch_id,
    is_supplier,
  });

const getCollectionsByWarehouse = (warehouse_id) =>
  apiFetch("POST", "/api/v1/amis/collection/by-warehouse", { warehouse_id });

const getCollectionsByOrganization = (organization_id, is_supplier = true) =>
  apiFetch("POST", "/api/v1/amis/collection/list", {
    organization_id,
    is_supplier,
  });

const updateCollectionStatus = (collection_id, collection_status_id) =>
  apiFetch("POST", "/api/v1/amis/collection/update-status", {
    collection_id: parseInt(collection_id, 10),
    collection_status_id: parseInt(collection_status_id, 10),
  });

const fetchCollectionsForUser = async () => {
  const scope = getUserScope();
  const orgId = getOrgId();

  let raw;
  if (scope.type === "branch") {
    raw = await getCollectionsByBranch(scope.id, scope.isSupplier);
  } else if (scope.type === "supplier_branch") {
    raw = await getCollectionsByBranch(scope.id, true);
  } else if (scope.type === "warehouse") {
    raw = await getCollectionsByWarehouse(scope.id);
  } else {
    const res = await getCollectionsByOrganization(orgId, false);
    raw = Array.isArray(res) ? res : (res?.collections ?? []);
  }

  return Array.isArray(raw) ? raw : (raw?.collections ?? []);
};

const STATUS_META = {
  New: {
    color: "var(--amber)",
    bg: "rgba(255,171,0,.06)",
    border: "rgba(255,171,0,.18)",
    icon: "bi-star",
  },
  Pending: {
    color: "var(--amber)",
    bg: "rgba(255,171,0,.1)",
    border: "rgba(255,171,0,.25)",
    icon: "bi-hourglass-split",
  },
  Confirmed: {
    color: "var(--blue)",
    bg: "rgba(64,196,255,.1)",
    border: "rgba(64,196,255,.25)",
    icon: "bi-check-circle",
  },
  Ready: {
    color: "var(--teal)",
    bg: "rgba(29,233,182,.1)",
    border: "rgba(29,233,182,.25)",
    icon: "bi-box-seam",
  },
  "In Transit": {
    color: "var(--blue)",
    bg: "rgba(64,196,255,.1)",
    border: "rgba(64,196,255,.25)",
    icon: "bi-truck",
  },
  Collected: {
    color: "var(--success)",
    bg: "rgba(0,230,118,.1)",
    border: "rgba(0,230,118,.25)",
    icon: "bi-check2-all",
  },
  Cancelled: {
    color: "var(--danger)",
    bg: "rgba(255,23,68,.1)",
    border: "rgba(255,23,68,.25)",
    icon: "bi-x-circle",
  },
};

const STATUS_ID_MAP = {
  New: 1,
  Pending: 1,
  Confirmed: 2,
  Ready: 3,
  "In Transit": 4,
  Collected: 5,
  Cancelled: 6,
};

const SUPPLIER_TRANSITIONS = { New: "Pending", Pending: "Confirmed" };
const CUSTOMER_TRANSITIONS = { New: "Pending", Pending: "Confirmed" };

const normaliseCollection = (raw) => {
  if (!raw) return null;
  const details = raw.details ?? raw.items ?? raw.details_list ?? [];
  const firstDetail = details[0] ?? {};
  return {
    id: raw.collection_id ?? raw.id,
    collectionNumber: raw.collection_number ?? null,
    status:
      raw.status?.description ??
      raw.status ??
      raw.collection_status?.description ??
      "Pending",
    statusId:
      raw.status?.collection_status_id ??
      raw.collection_status?.collection_status_id ??
      STATUS_ID_MAP[raw.status ?? "Pending"] ??
      1,
    collectionDate: raw.collection_date,
    type: raw.collection_type ?? raw.type ?? "Collection",
    notes: raw.notes,
    customerBranchId: raw.customer_branch_id,
    customerBranch:
      raw.customer_branch?.branch_name ?? raw.customer_branch_name ?? null,
    supplierBranchId: raw.supplier_branch_id,
    supplierBranch:
      raw.supplier_branch?.branch_name ?? raw.supplier_branch_name ?? null,
    warehouseId: raw.source_warehouse_id,
    depot:
      raw.warehouse?.warehouse_name ??
      raw.source_warehouse?.warehouse_name ??
      raw.depot ??
      "—",
    product:
      firstDetail.asset_type_description ??
      firstDetail.asset_description ??
      null,
    qty: firstDetail.quantity ?? firstDetail.qty ?? null,
    unit: firstDetail.unit ?? "cylinders",
    details: Array.isArray(details)
      ? details.map((d) => ({
          ...d,
          collection_detail_type:
            d.collection_detail_type ?? d.detail_type ?? null,
        }))
      : [],
    customerBranchRaw: raw.customer_branch ?? raw.customer_branch_obj ?? null,
    supplierBranchRaw: raw.supplier_branch ?? raw.supplier_branch_obj ?? null,
    sourceWarehouseRaw: raw.source_warehouse ?? raw.warehouse ?? null,
    createdByRaw: raw.created_by ?? raw.createdBy ?? null,
    creationDateRaw: raw.creation_date ?? raw.creationDate ?? null,
    geoLocation: raw.geo_location ?? raw.geoLocation ?? null,
    collectionAddress: raw.address_line_1
      ? {
          street: raw.address_line_1,
          suburb: raw.address_line_2 ?? "",
          city: raw.city ?? "",
          postalCode: raw.postal_code ?? "",
          province: raw.region ?? "",
          country: raw.country ?? "",
        }
      : (raw.collection_address ?? null),
    CollectionAddress: raw.address_line_1
      ? {
          street: raw.address_line_1,
          suburb: raw.address_line_2 ?? "",
          city: raw.city ?? "",
          postalCode: raw.postal_code ?? "",
          province: raw.region ?? "",
          country: raw.country ?? "",
        }
      : (raw.collection_address ?? null),
    total: raw.total ?? null,
    invoiceId: raw.invoice_id ?? null,
    driver: raw.driver_name ?? null,
    vehicle: raw.vehicle_reg ?? null,
    damagedBottles: {
      reported: details.some((d) => d.is_faulty || d.damaged_qty > 0),
      qty: details.reduce((acc, d) => acc + (d.damaged_qty ?? 0), 0),
    },
    _raw: raw,
  };
};

// ─── Shared UI ───────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META["Pending"];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: s.color,
          display: "inline-block",
        }}
      />
      {status}
    </span>
  );
}

function DamagedBadge({ damagedBottles }) {
  if (!damagedBottles?.reported) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 7px",
        borderRadius: 20,
        fontSize: 9,
        fontWeight: 700,
        color: "var(--danger)",
        background: "rgba(255,23,68,.1)",
        border: "1px solid rgba(255,23,68,.25)",
      }}
    >
      <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 8 }} />
      {damagedBottles.qty} faulty
    </span>
  );
}

// ─── Delivery Note Modal ───────────────────────────────────────────────────────
function DeliveryNote({ collection, onClose }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        backdropFilter: "blur(8px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          width: "90%",
          maxWidth: 640,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 18px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button className="btn btn-s btn-sm" onClick={() => window.print()}>
            <i className="bi bi-printer" /> Print
          </button>
          <button className="btn btn-g btn-sm btn-ico" onClick={onClose}>
            <i
              className="bi bi-x"
              style={{ fontSize: 14, marginLeft: 4, marginTop: 2 }}
            />
          </button>
        </div>
        <div
          id="delivery-note"
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "32px 40px",
            background: "var(--surface)",
            color: "#111",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 32,
              paddingBottom: 20,
              borderBottom: "3px solid #ff4f17",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 900,
                  fontSize: 22,
                  color: "#ff4f17",
                  letterSpacing: ".04em",
                }}
              >
                GasCRM
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                Industrial Gas Supply &amp; Distribution
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "#999",
                  marginTop: 8,
                  lineHeight: 1.6,
                }}
              >
                14 Industrial Road, Johannesburg North
                <br />
                Tel: +27 11 555 0100 · info@Beta.co.za
                <br />
                VAT No: 4230198765
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 900,
                  color: "#111",
                  marginBottom: 4,
                }}
              >
                DELIVERY NOTE
              </div>
              <div style={{ fontSize: 12, color: "#555" }}>
                No:{" "}
                <strong>{collection.collectionNumber ?? collection.id}</strong>
              </div>
              <div style={{ fontSize: 12, color: "#555" }}>
                Date: <strong>{collection.collectionDate}</strong>
              </div>
              {collection.invoiceId && (
                <div style={{ fontSize: 12, color: "#555" }}>
                  Invoice: <strong>{collection.invoiceId}</strong>
                </div>
              )}
            </div>
          </div>
          {/* ... rest of delivery note unchanged ... */}
        </div>
      </div>
    </div>
  );
}

// ─── Collection Detail Page ────────────────────────────────────────────────────
function CollectionDetailPage({
  collection,
  onBack,
  onStatusAdvance,
  onCancel,
  showToast: toast,
}) {
  const [showNote, setShowNote] = useState(false);
  const [fullData, setFullData] = useState(collection);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const { ready, can } = usePermissions();
  const scope = getUserScope();
  const isSupplier = scope.isSupplier;

  useEffect(() => {
    if (!collection.id) return;
    setLoadingDetail(true);
    getCollectionById(collection.id)
      .then((raw) => {
        if (raw) setFullData(normaliseCollection(raw));
      })
      .catch(() => {})
      .finally(() => setLoadingDetail(false));
  }, [collection.id]);

  if (!ready) return null;

  const c = fullData;
  const transitions = isSupplier ? SUPPLIER_TRANSITIONS : CUSTOMER_TRANSITIONS;
  const nextStatus = transitions[c.status] ?? null;

  const canAdvance =
    can("COLLECTION", "UPDATE") &&
    !!nextStatus &&
    c.status !== "Collected" &&
    c.status !== "Cancelled";

  const canCancel =
    can("COLLECTION", "DELETE") &&
    c.status !== "Collected" &&
    c.status !== "Cancelled";

  const canViewDetail = can("COLLECTION", "READ");

  // ── Small helpers ──────────────────────────────────────────────────────────
  const DetailRow = ({ label, value, accent }) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12,
        padding: "8px 0",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <span style={{ fontSize: 11, color: "var(--text3)", flexShrink: 0 }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: accent ?? "var(--text1)",
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value ?? "—"}
      </span>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--text3)",
        padding: "12px 0 6px",
        borderBottom: "1px solid var(--border)",
        marginBottom: 2,
      }}
    >
      {children}
    </div>
  );

  const DETAIL_TYPE = {
    1: {
      label: "Standard",
      color: "var(--teal)",
      bg: "rgba(29,233,182,.1)",
      border: "rgba(29,233,182,.25)",
    },
    2: {
      label: "Damaged",
      color: "var(--danger)",
      bg: "rgba(255,23,68,.1)",
      border: "rgba(255,23,68,.25)",
    },
  };

  const TypeBadge = ({ typeNum }) => {
    const t = DETAIL_TYPE[typeNum];
    if (!t) return <span className="fs10 tc3">—</span>;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          borderRadius: 20,
          fontSize: 10,
          fontWeight: 700,
          color: t.color,
          background: t.bg,
          border: `1px solid ${t.border}`,
        }}
      >
        {t.label}
      </span>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="page">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          padding: "18px 22px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 22 15"
            fill="none"
            stroke="currentColor"
            color="#99E4D5"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>

        {/* Identity */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span className="syne fw7" style={{ fontSize: 18 }}>
              {isSupplier
                ? (c.customerBranch ?? "—")
                : (c.supplierBranch ?? "—")}
            </span>
            <span className="mono t-flame" style={{ fontSize: 11 }}>
              {c.collectionNumber ?? c.id}
            </span>
            <StatusPill status={c.status} />
            <span className="tag">{c.type}</span>
            <DamagedBadge damagedBottles={c.damagedBottles} />
          </div>
          <div className="fc gap12" style={{ marginTop: 5, flexWrap: "wrap" }}>
            <span className="fs11 tc2">
              <i className="bi bi-geo-alt" style={{ marginRight: 4 }} />
              {c.depot}
            </span>
            <span className="fs11 tc2">
              <i className="bi bi-calendar3" style={{ marginRight: 4 }} />
              {c.collectionDate}
            </span>
            {c.details?.length > 0 && (
              <span className="fs11 tc2">
                <i className="bi bi-box-seam" style={{ marginRight: 4 }} />
                {c.details.length} line item{c.details.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>

        {/* Inline action buttons */}
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            flexShrink: 0,
            alignItems: "center",
          }}
        >
          {isSupplier && canAdvance && (
            <button
              className="btn btn-p btn-sm"
              onClick={() => onStatusAdvance(c)}
            >
              <i className="bi bi-arrow-right-circle" /> Mark as {nextStatus}
            </button>
          )}
          {!isSupplier && canAdvance && c.status === "Pending" && (
            <button
              className="btn btn-p btn-sm"
              onClick={() => onStatusAdvance(c)}
            >
              <i className="bi bi-check-circle" /> Confirm
            </button>
          )}
          {canCancel && (
            <button
              className="btn btn-d btn-sm"
              onClick={() => {
                if (
                  window.confirm(
                    `Cancel collection ${c.collectionNumber ?? c.id}? This cannot be undone.`,
                  )
                )
                  onCancel(c);
              }}
            >
              <i className="bi bi-x-circle" /> Cancel
            </button>
          )}
        </div>
      </div>

      {/* ── Customer & Supplier side-by-side ─────────────────────────────── */}
      <div className="g2" style={{ marginBottom: 16, alignItems: "stretch" }}>
        {/* Customer Details */}
        <div
          className="panel"
          style={{ display: "flex", flexDirection: "column", marginBottom: 0 }}
        >
          <div className="ph">
            <i
              className="bi bi-building"
              style={{ fontSize: 13, color: "var(--blue)" }}
            />
            <span className="ph-title">Customer Details</span>
          </div>
          <div style={{ padding: "4px 18px 16px", flex: 1 }}>
            <DetailRow label="Branch" value={c.customerBranch} />
            <DetailRow
              label="Organisation"
              value={c.customerBranchRaw?.organization?.name}
            />
            <DetailRow
              label="VAT Number"
              value={c.customerBranchRaw?.vat_registration}
            />
            <DetailRow
              label="Company Reg"
              value={c.customerBranchRaw?.company_registration}
            />
            {c.customerBranchRaw?.branch_customer_id != null && (
              <DetailRow
                label="Customer ID"
                value={c.customerBranchRaw.branch_customer_id}
              />
            )}

            {/* Collection address */}
            {c.CollectionAddress && (
              <>
                <SectionLabel>Collection Address</SectionLabel>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    paddingTop: 6,
                  }}
                >
                  <i
                    className="bi bi-geo-alt-fill"
                    style={{
                      color: "var(--teal)",
                      marginTop: 2,
                      flexShrink: 0,
                    }}
                  />
                  <div className="fs12 tc2" style={{ lineHeight: 1.8 }}>
                    {c.CollectionAddress.street && (
                      <div>{c.CollectionAddress.street}</div>
                    )}
                    {(c.CollectionAddress.suburb ||
                      c.CollectionAddress.city) && (
                      <div>
                        {[c.CollectionAddress.suburb, c.CollectionAddress.city]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                    {(c.CollectionAddress.postalCode ||
                      c.CollectionAddress.province) && (
                      <div>
                        {[
                          c.CollectionAddress.postalCode,
                          c.CollectionAddress.province,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                    {c.CollectionAddress.country && (
                      <div className="tc3">{c.CollectionAddress.country}</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Supplier Details */}
        <div
          className="panel"
          style={{ display: "flex", flexDirection: "column", marginBottom: 0 }}
        >
          <div className="ph">
            <i
              className="bi bi-truck"
              style={{ fontSize: 13, color: "var(--teal)" }}
            />
            <span className="ph-title">Supplier Details</span>
          </div>
          <div style={{ padding: "4px 18px 16px", flex: 1 }}>
            <DetailRow label="Branch" value={c.supplierBranch} />
            <DetailRow
              label="Organisation"
              value={c.supplierBranchRaw?.organization?.name}
            />
            <DetailRow
              label="VAT Number"
              value={c.supplierBranchRaw?.vat_registration}
            />
            <DetailRow
              label="Company Reg"
              value={c.supplierBranchRaw?.company_registration}
            />
            <DetailRow label="Source Warehouse" value={c.depot} />

            {c.driver && <DetailRow label="Driver" value={c.driver} />}
            {c.vehicle && <DetailRow label="Vehicle" value={c.vehicle} />}
            {c.invoiceId && (
              <DetailRow
                label="Invoice"
                value={c.invoiceId}
                accent="var(--flame)"
              />
            )}

            {/* Creation meta */}
            {(c.createdByRaw || c.creationDateRaw) && (
              <>
                <SectionLabel>Record Info</SectionLabel>
                {c.createdByRaw && (
                  <DetailRow
                    label="Created By"
                    value={`${c.createdByRaw.name ?? ""} ${c.createdByRaw.surname ?? ""}`.trim()}
                  />
                )}
                {c.creationDateRaw && (
                  <DetailRow
                    label="Created On"
                    value={fmt.date(c.creationDateRaw)}
                  />
                )}
              </>
            )}

            {c.notes && (
              <>
                <SectionLabel>Notes</SectionLabel>
                <p
                  className="fs12 tc2"
                  style={{ paddingTop: 6, lineHeight: 1.6 }}
                >
                  {c.notes}
                </p>
              </>
            )}

            {/* Damaged summary */}
            {c.damagedBottles?.reported && (
              <>
                <SectionLabel>Damage Report</SectionLabel>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    paddingTop: 8,
                    color: "var(--danger)",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  <i
                    className="bi bi-exclamation-triangle-fill"
                    style={{ fontSize: 13 }}
                  />
                  {c.damagedBottles.qty} cylinder
                  {c.damagedBottles.qty !== 1 ? "s" : ""} reported faulty
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Line Items (full width) ───────────────────────────────────────── */}
      {(c.details ?? []).length > 0 && (
        <div className="panel">
          <div className="ph">
            <i
              className="bi bi-list-ul"
              style={{ fontSize: 13, color: "var(--flame)" }}
            />
            <span className="ph-title">Line Items</span>
            <span className="ph-sub">
              {c.details.length} item{c.details.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Qty</th>
                  <th style={{ textAlign: "right" }}></th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {c.details.map((d, i) => (
                  <tr key={d.collection_detail_id ?? i}>
                    <td
                      className="mono fs10 tc3"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {d.asset_type_code ?? "—"}
                    </td>
                    <td className="fs12">
                      {d.asset_type_description ?? d.asset_description ?? "—"}
                    </td>
                    <td
                      className="mono fw7"
                      style={{ textAlign: "right", color: "var(--text1)" }}
                    >
                      {d.quantity ?? d.qty ?? "—"}
                    </td>
                    <td></td>
                    <td>
                      <TypeBadge
                        typeNum={d.collection_detail_type ?? d.detail_type}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showNote && (
        <DeliveryNote collection={c} onClose={() => setShowNote(false)} />
      )}
    </div>
  );
}

// ─── Main Collection Page ──────────────────────────────────────────────────────
function CollectionPage({ showToast: toast }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list");
  const [detail, setDetail] = useState(null);

  const {
    ready,
    canSeePage,
    canViewCollections,
    canCreateCollections,
    canUpdateCollections,
    canDeleteCollections,
  } = usePermissions();

  const scope = getUserScope();
  const isSupplier = scope.isSupplier;

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchCollectionsForUser()
      .then((rawArr) =>
        setCollections((rawArr ?? []).map(normaliseCollection).filter(Boolean)),
      )
      .catch((e) => {
        console.error("fetchCollections:", e);
        setError(e.message ?? "Failed to load collections");
        toast("Failed to load collections", "e");
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Re-fetches the full collection list from the server (server-truth refresh,
  //     used after any mutation instead of trusting an optimistic local patch) ──
  // NOTE: declared before handleCreated so it is in scope for the callback below.
  const refreshCollections = useCallback(async () => {
    try {
      const rawArr = await fetchCollectionsForUser();
      setCollections((rawArr ?? []).map(normaliseCollection).filter(Boolean));
    } catch (e) {
      console.error("refreshCollections:", e);
      toast("Failed to refresh collections", "e");
    }
  }, [toast]);

  // ── After a new collection is created: navigate to the list immediately,
  //     show the success toast, then pull fresh data from the server so the
  //     newly created record appears with its real server-assigned ID/number. ──
  const handleCreated = useCallback(
    async (_newRaw) => {
      setView("list");
      toast("Collection created successfully", "s");
      await refreshCollections();
    },
    [refreshCollections, toast],
  );

  const advanceStatus = useCallback(
    async (col) => {
      const scope2 = getUserScope();
      const transitions = scope2.isSupplier
        ? SUPPLIER_TRANSITIONS
        : CUSTOMER_TRANSITIONS;
      const next = transitions[col.status];
      if (!next) {
        toast("No next status available", "w");
        return;
      }
      const statusId = STATUS_ID_MAP[next];
      if (!statusId) {
        toast(`Unknown status: ${next}`, "e");
        return;
      }
      try {
        const result = await updateCollectionStatus(col.id, statusId);
        if (!result) {
          toast(`Failed to update ${col.id}`, "e");
          return;
        }

        // Pull fresh data from the server instead of patching local state by hand
        await refreshCollections();

        // Safety net: keep the detail view's status badge in sync in case the
        // refreshed list lookup ever falls back to stale `detail` state
        if (detail?.id === col.id) {
          setDetail((d) => ({ ...d, status: next, statusId }));
        }

        toast(`${col.collectionNumber ?? col.id} → ${next}`, "s");
      } catch (e) {
        toast(`Failed to advance status: ${e.message}`, "e");
      }
    },
    [detail, refreshCollections, toast],
  );

  const cancelCollection = useCallback(
    async (col) => {
      const statusId = STATUS_ID_MAP["Cancelled"];
      try {
        const result = await updateCollectionStatus(col.id, statusId);
        if (!result) {
          toast(`Failed to cancel ${col.id}`, "e");
          return;
        }

        // Send the user back to the list view immediately — cancel is terminal,
        // there's nothing left to look at on the detail page
        setView("list");
        setDetail(null);

        // Then pull fresh data from the server so the list reflects the real,
        // current state rather than a hand-patched guess
        await refreshCollections();

        toast(`${col.collectionNumber ?? col.id} cancelled`, "s");
      } catch (e) {
        toast(`Failed to cancel: ${e.message}`, "e");
      }
    },
    [refreshCollections, toast],
  );

  const filtered = collections.filter((c) => {
    const q = search.toLowerCase();
    const matchQ =
      (c.collectionNumber ?? "").toLowerCase().includes(q) ||
      String(c.id).toLowerCase().includes(q) ||
      (c.customerBranch ?? "").toLowerCase().includes(q) ||
      (c.supplierBranch ?? "").toLowerCase().includes(q) ||
      (c.product ?? "").toLowerCase().includes(q);
    return matchQ && (filterStatus === "All" || c.status === filterStatus);
  });

  // ✅ Guard 1: wait for permissions to resolve before doing anything
  if (!ready) return null;

  // ✅ Guard 2: page-level access check — hides the whole page if not permitted
  if (!canSeePage("collection")) return null;

  // ✅ Guard 3: no READ and no CREATE = show a proper locked state
  if (!canViewCollections && !canCreateCollections) {
    return (
      <div className="page">
        <div
          className="panel"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text3)",
            fontSize: 13,
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 12 }}>🔒</div>
          You don't have permission to view collections.
        </div>
      </div>
    );
  }

  if (view === "create") {
    // ✅ Extra safety: don't render the create page if CREATE was somehow reached without permission
    if (!canCreateCollections) {
      setView("list");
      return null;
    }
    return (
      <CreateCollectionPage
        onBack={() => setView("list")}
        onCreated={handleCreated}
        showToast={toast}
        isSupplier={isSupplier}
        userScope={scope}
      />
    );
  }

  if (view === "detail" && detail) {
    const live = collections.find((c) => c.id === detail.id) || detail;
    return (
      <CollectionDetailPage
        collection={live}
        onBack={() => {
          setView("list");
          setDetail(null);
        }}
        onStatusAdvance={advanceStatus}
        onCancel={cancelCollection}
        showToast={toast}
      />
    );
  }

  return (
    <div className="page">
      <div className="panel">
        <div className="tbar">
          <div className="tsearch">
            <i
              className="bi bi-search"
              style={{ color: "var(--text3)", fontSize: 12 }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, branch, product…"
            />
          </div>
          <select
            className="tfilter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {[
              "All",
              "Pending",
              "Confirmed",
              "Ready",
              "In Transit",
              "Collected",
              "Cancelled",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          {/* ✅ Create button only rendered when user has CREATE permission */}
          {canCreateCollections && (
            <button className="btn btn-p" onClick={() => setView("create")}>
              <i className="bi bi-plus-lg" /> New Collection
            </button>
          )}

          <span
            className="fs11 mono"
            style={{ marginLeft: "auto", color: "var(--text3)" }}
          >
            {filtered.length} of {collections.length}
          </span>
        </div>

        <table className="tbl">
          <thead>
            <tr>
              <th>ID</th>
              <th>{isSupplier ? "Customer Branch" : "Supplier Branch"}</th>
              <th>Depot</th>
              <th>Date</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty">
                    <div className="empty-ico" />
                    Loading collections…
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty" style={{ color: "var(--danger)" }}>
                    ⚠️ {error}
                  </div>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty">
                    <div className="empty-ico" />
                    No collections found
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr
                  key={c.id}
                  // ✅ Rows are only clickable if the user can READ detail
                  style={{ cursor: canViewCollections ? "pointer" : "default" }}
                  onClick={() => {
                    if (!canViewCollections) return;
                    setDetail(c);
                    setView("detail");
                  }}
                >
                  <td className="mono t-flame" style={{ fontSize: 11 }}>
                    {c.collectionNumber ?? c.id}
                  </td>
                  <td
                    className="fs12 fw6"
                    style={{
                      maxWidth: 150,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isSupplier
                      ? (c.customerBranch ?? "—")
                      : (c.supplierBranch ?? "—")}
                  </td>
                  <td
                    className="fs11 tc2"
                    style={{
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.depot}
                  </td>
                  <td className="mono fs10 tc3">{c.collectionDate}</td>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <StatusPill status={c.status} />
                      {c.damagedBottles?.reported && (
                        <i
                          className="bi bi-exclamation-triangle-fill"
                          style={{ color: "var(--danger)", fontSize: 10 }}
                          title={`${c.damagedBottles.qty} faulty cylinders`}
                        />
                      )}
                    </div>
                  </td>
                  {/* ✅ Chevron only shown if the user can actually open the row */}
                  <td>
                    {canViewCollections && (
                      <i
                        className="bi bi-chevron-right"
                        style={{ fontSize: 10, color: "var(--text3)" }}
                      />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CollectionPage;
